import {
  isGeneratedNodeId,
  type EdgeLayout,
  type FlowPositionItem,
  type GeneratedNodePosition,
  type GetProcessLayoutResponse,
  type StepPosition,
  type UpdateProcessLayoutPatch
} from '@bliks-io/contracts';
import { makeEdgeKey } from '@bliks-io/ui';
import { isObjectIdHex } from '@/lib/utils';

export interface EdgeLayoutPatchInput {
  sourceNodeId: string;
  targetNodeId: string;
  sourceHandleId: string | null;
  waypoints: Array<{ id: string; x: number; y: number }>;
}

// A generated node of an optimistic node (`end-temp-draft-step-…`) is renamed
// client-side once the create resolves; a stored entry under the temp-derived
// id would never be read again.
const isTempDerivedNodeId = (nodeId: string): boolean => nodeId.includes('temp-draft-');

function partitionPositions(positions: FlowPositionItem[]) {
  const stepUpdates = new Map<string, { x: number; y: number }>();
  const genUpdates = new Map<string, { x: number; y: number }>();
  for (const p of positions) {
    if (isGeneratedNodeId(p.stepId)) {
      if (!isTempDerivedNodeId(p.stepId)) genUpdates.set(p.stepId, { x: p.x, y: p.y });
    } else if (isObjectIdHex(p.stepId)) {
      // A drag of a node still carrying its optimistic temp id is dropped: the
      // backend rejects the whole patch for it, and the node keeps the
      // position its create wrote.
      stepUpdates.set(p.stepId, { x: p.x, y: p.y });
    }
  }
  return { stepUpdates, genUpdates };
}

/**
 * Build the layout patch for moved nodes and changed edges. Step positions
 * carry only the moved nodes — the endpoint upserts them per node. Generated
 * positions and edge layouts are replaced wholesale by the endpoint, so the
 * update is merged into the cached array and the full set is sent.
 *
 * Empty-waypoint edge updates are treated as deletions: the matching
 * `EdgeLayout` is dropped from the returned array rather than stored with
 * `waypoints: []`. This keeps the cached layout from carrying dead keys for
 * every edge the user has ever touched.
 */
export function buildLayoutPatchFromPositions(
  positions: FlowPositionItem[],
  cached: GetProcessLayoutResponse | undefined,
  edgeUpdates: EdgeLayoutPatchInput[] = []
): UpdateProcessLayoutPatch {
  const { stepUpdates, genUpdates } = partitionPositions(positions);

  const patch: UpdateProcessLayoutPatch = {};
  if (stepUpdates.size > 0) {
    patch.stepPositions = Array.from(stepUpdates, ([stableStepId, xy]): StepPosition => ({
      stableStepId,
      ...xy
    }));
  }
  if (genUpdates.size > 0) {
    const merged: GeneratedNodePosition[] = (cached?.generatedNodePositions ?? [])
      .filter((g) => !genUpdates.has(g.nodeId) && !isTempDerivedNodeId(g.nodeId))
      .map((g) => ({ nodeId: g.nodeId, x: g.x, y: g.y }));
    for (const [nodeId, xy] of genUpdates) merged.push({ nodeId, ...xy });
    patch.generatedNodePositions = merged;
  }
  if (edgeUpdates.length > 0) {
    const incomingByKey = new Map<string, EdgeLayoutPatchInput>();
    for (const u of edgeUpdates) {
      incomingByKey.set(makeEdgeKey(u.sourceNodeId, u.targetNodeId, u.sourceHandleId), u);
    }

    const merged: EdgeLayout[] = [];
    for (const e of cached?.edgeLayouts ?? []) {
      const k = makeEdgeKey(e.sourceNodeId, e.targetNodeId, e.sourceHandleId ?? null);
      if (!incomingByKey.has(k)) {
        merged.push({
          sourceNodeId: e.sourceNodeId,
          targetNodeId: e.targetNodeId,
          sourceHandleId: e.sourceHandleId ?? null,
          waypoints: e.waypoints.map((w) => ({ id: w.id, x: w.x, y: w.y }))
        });
      }
    }
    for (const u of edgeUpdates) {
      if (u.waypoints.length === 0) continue; // treat empty → deletion
      merged.push({
        sourceNodeId: u.sourceNodeId,
        targetNodeId: u.targetNodeId,
        sourceHandleId: u.sourceHandleId,
        waypoints: u.waypoints.map((w) => ({ id: w.id, x: w.x, y: w.y }))
      });
    }
    patch.edgeLayouts = merged;
  }
  return patch;
}
